import React, { useEffect, useMemo, useRef, useState } from "react";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Button from "@amzn/awsui-components-react/polaris/button";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Form from "@amzn/awsui-components-react/polaris/form";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Link from "@amzn/awsui-components-react/polaris/link";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import SplitPanel from "@amzn/awsui-components-react/polaris/split-panel";
import StatusIndicator from "@amzn/awsui-components-react/polaris/status-indicator";
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledSelect,
  ControlledTextarea,
} from "@amzn/react-hook-form-polaris";
import { FormProvider, get, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAppDispatch, useAppSelector } from "app/store";
import { useNotifications } from "features/notifications/hooks";
import { useGaiaObjectUiContent } from "features/ui-content";
import { useSelectedTeam } from "features/team-settings/hooks";
import { getAuditById } from "features/audits/slice";
import { getGaiaObjectById } from "features/gaiaObjects/slice";
import { updateGaiaObjectStatusSchema } from "models/schemas";
import { Audit } from "models/generateAuditSchemas";
import { GaiaObject } from "models/generateGaiaObjSchemas";
import { CustomAttributes } from "models/generateCustomAttributesSchema";
import { ProgressUpdateAttachment } from "models/schemas";
import UploadFile from "components/upload-file";
import { useUploadUrlMutation } from "components/documents-table-os/endpoints";
import {
  ClosingRequirement,
  ClosingRequirementResult,
  OUTCOME_REQUIREMENT_TYPES,
} from "components/closing-requirements/definitions";
import {
  useEvaluateClosingRequirementsMutation,
  useGetClosingRequirementsQuery,
} from "components/closing-requirements/endpoints";
import ClosingRequirementsChecklist from "components/closing-requirements-checklist";
import { ClosingRequirementValues } from "components/closing-requirements-checklist/definitions";
import {
  pluralise,
  unmetCount,
} from "components/closing-requirements-checklist/utils";
import { useDebounce } from "components/server-side-table/hooks";
import { getQueryErrorMessage } from "utils/error-message-utils";
import { transformCustomAttributesDataMap } from "utils/dynamicAttributesUtils";
import {
  buildS3Key,
  createUploadUrlRequest,
  uploadFileToS3,
} from "utils/document-utils";
import {
  cap,
  toUnixEpochTime,
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import {
  useUpdateAuditProgressMutation,
  useUpdateGaiaObjectProgressMutation,
} from "./endpoints";
import {
  buildAuditUpdate,
  buildGaiaObjectUpdate,
  closeDateAttribute,
  initialFormValues,
  toSaveFailure,
  withoutCloseDate,
} from "./utils";
import { useProgressUpdateSplitPanel } from "./hooks";
import {
  ProgressUpdateCategory,
  ProgressUpdateFormValues,
  ProgressUpdateSaveResult,
} from "./definitions";

type UploadStatus = "pending" | "uploading" | "success" | "error";

const UPLOAD_STATUS_INDICATOR = {
  pending: "pending",
  uploading: "in-progress",
  success: "success",
  error: "error",
} as const;

const COMPLETE_STATUS = "complete";
const EVALUATE_DEBOUNCE_MS = 400;

const isComplete = (status: string | undefined) =>
  status?.toLowerCase() === COMPLETE_STATUS;

const progressUpdateSchema = updateGaiaObjectStatusSchema.extend({
  closeDate: z.string(),
});

const requirementList = (requirements: ClosingRequirementResult[]) => (
  <ul>
    {requirements.map((requirement) => (
      <li key={requirement.requirementId}>
        {requirement.reason
          ? `${requirement.title}: ${requirement.reason}`
          : requirement.title}
      </li>
    ))}
  </ul>
);

const ProgressUpdateSplitPanel: React.FC = () => {
  const { splitPanel, close } = useProgressUpdateSplitPanel();
  const category = splitPanel.category as ProgressUpdateCategory;
  const navigate = useNavigate();
  const selectedAudit = useAppSelector((state) => state.audits.selectedAudit) as
    | Audit
    | undefined;
  const selectedGaiaObject = useAppSelector(
    (state) =>
      (state.gaiaObjects.selectedGaiaObject as { gaiaObject?: GaiaObject })
        ?.gaiaObject,
  );
  const teamCustomAttributesSchema = useAppSelector(
    (state) =>
      state.teamSettings.selectedTeamMainMetaData.teamCustomAttributesSchema,
  );
  const record = category === "audit" ? selectedAudit : selectedGaiaObject;
  const recordId =
    category === "audit"
      ? (record as Audit | undefined)?.auditId
      : (record as GaiaObject | undefined)?.gaiaObjectId;
  const customAttributes = teamCustomAttributesSchema?.[category] as
    | CustomAttributes
    | undefined;
  const attribute = closeDateAttribute(customAttributes);
  const showCloseDate = category === "audit" || attribute !== null;

  const uiContent = useGaiaObjectUiContent(category);
  const { isProgressUpdateFilesEnabled } = useSelectedTeam();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const [uploadUrl] = useUploadUrlMutation();
  const [updateAuditProgress] = useUpdateAuditProgressMutation();
  const [updateGaiaObjectProgress] = useUpdateGaiaObjectProgressMutation();

  const [files, setFiles] = useState<File[]>([]);
  const [uploadStatuses, setUploadStatuses] = useState<Map<File, UploadStatus>>(
    new Map(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const uploaded = useRef(new Map<File, ProgressUpdateAttachment>());
  const [unmetRequirements, setUnmetRequirements] = useState<
    ClosingRequirementResult[]
  >([]);
  const [values, setValues] = useState<ClosingRequirementValues>({});
  const [results, setResults] = useState<ClosingRequirementResult[]>();
  const [evaluateError, setEvaluateError] = useState<string>();
  const [evaluatedInput, setEvaluatedInput] = useState<string>();
  const [recheck, setRecheck] = useState(0);
  const [evaluateClosingRequirements] =
    useEvaluateClosingRequirementsMutation();
  const latestEvaluation = useRef(0);

  const defaultValues = useMemo(
    () => initialFormValues(category, record, attribute),
    [category, record, attribute],
  );
  const formMethods = useForm<ProgressUpdateFormValues>({
    defaultValues,
    mode: "onBlur",
    resolver: zodResolver(progressUpdateSchema),
  });

  useEffect(() => {
    formMethods.reset(defaultValues);
    uploaded.current = new Map();
    setFiles([]);
    setUploadStatuses(new Map());
    setUnmetRequirements([]);
    setValues({});
    setResults(undefined);
    setEvaluateError(undefined);
    setEvaluatedInput(undefined);
  }, [recordId]);

  const status = formMethods.watch("status");
  const closeDate = formMethods.watch("closeDate");
  const checklistActive =
    isComplete(status) && !isComplete(defaultValues.status);
  const documentsParentId =
    category === "audit"
      ? recordId
      : (record as GaiaObject | undefined)?.shortId;

  const config = useGetClosingRequirementsQuery(
    { teamName: record?.teamName ?? "" },
    { skip: !checklistActive || !record?.teamName },
  );
  const requirements = useMemo(
    () =>
      Object.fromEntries(
        (
          config.data?.configs.find(
            (candidate) => candidate.gaiaObjectType === category,
          )?.requirements ?? []
        ).map((requirement): [string, ClosingRequirement] => [
          requirement.requirementId,
          requirement,
        ]),
      ),
    [config.data, category],
  );

  useEffect(() => {
    if (checklistActive) {
      setResults(undefined);
      setEvaluateError(undefined);
      setEvaluatedInput(undefined);
    }
  }, [checklistActive]);

  const evaluationInput = JSON.stringify({ closeDate, values, recheck });
  const debouncedInput = useDebounce(evaluationInput, EVALUATE_DEBOUNCE_MS);

  useEffect(() => {
    if (!checklistActive || !recordId) return;
    const sequence = ++latestEvaluation.current;
    setEvaluateError(undefined);
    const input = JSON.parse(debouncedInput) as {
      closeDate: string;
      values: ClosingRequirementValues;
    };
    evaluateClosingRequirements({
      gaiaObjectId: recordId,
      ...(input.closeDate
        ? { closeDate: toUnixEpochTime(input.closeDate) }
        : {}),
      closingRequirementValues: Object.values(input.values),
    })
      .unwrap()
      .then((response) => {
        if (sequence !== latestEvaluation.current) return;
        setResults(response.results);
        setEvaluateError(undefined);
        setEvaluatedInput(debouncedInput);
      })
      .catch((error: unknown) => {
        if (sequence !== latestEvaluation.current) return;
        setResults(undefined);
        setEvaluateError(
          getQueryErrorMessage(error, "The check didn't complete."),
        );
        setEvaluatedInput(debouncedInput);
      });
  }, [debouncedInput, checklistActive, recordId]);

  const isEvaluating = checklistActive && evaluatedInput !== evaluationInput;
  const checkedResults =
    showCloseDate && !closeDate ? withoutCloseDate(results) : results;
  const blockingUnmet = (checkedResults ?? []).some(
    (result) => !result.met && result.severity === "BLOCK",
  );
  const saveBlocked =
    checklistActive &&
    (isEvaluating || (!evaluateError && (!checkedResults || blockingUnmet)));

  const blocking = unmetCount(checkedResults, "BLOCK");
  const warnings = unmetCount(checkedResults, "WARNING");
  const viewOutcomes = () => navigate({ search: "?tabId=outcomes" });
  const outcomeUnmet = unmetRequirements.some((requirement) =>
    OUTCOME_REQUIREMENT_TYPES.includes(requirement.requirementType),
  );

  useEffect(() => {
    setUnmetRequirements([]);
  }, [closeDate]);

  const onRequirementValueChange = (
    requirementId: string,
    value: ClosingRequirementValues[string] | null,
  ) => {
    setUnmetRequirements([]);
    setValues((current) => {
      const next = { ...current };
      if (value) {
        next[requirementId] = value;
      } else {
        delete next[requirementId];
      }
      return next;
    });
  };

  const uploadAttachments = async (): Promise<ProgressUpdateAttachment[]> => {
    const prefix = `${category}s/${recordId}`;
    const attachments: ProgressUpdateAttachment[] = [];
    for (const file of files) {
      const previous = uploaded.current.get(file);
      if (previous) {
        attachments.push(previous);
        continue;
      }
      setUploadStatuses((prev) => new Map(prev).set(file, "uploading"));
      try {
        const response = await uploadUrl(
          createUploadUrlRequest(
            prefix,
            file.name,
            record?.teamName ?? "",
            "Normal",
          ),
        ).unwrap();
        await uploadFileToS3(file, response);
        const attachment = {
          s3Key: buildS3Key(prefix, response.uniqueKey, file.name),
          fileName: getFileNameWithoutExtension(file.name),
          extensionType: getFileExtension(file.name),
          size: getFileSize(file.size),
        };
        uploaded.current.set(file, attachment);
        attachments.push(attachment);
        setUploadStatuses((prev) => new Map(prev).set(file, "success"));
      } catch (e) {
        setUploadStatuses((prev) => new Map(prev).set(file, "error"));
        throw e;
      }
    }
    return attachments;
  };

  const save = async (
    data: ProgressUpdateFormValues,
  ): Promise<ProgressUpdateSaveResult> => {
    try {
      const attachments = isProgressUpdateFilesEnabled
        ? await uploadAttachments()
        : undefined;
      const closingRequirementValues = checklistActive
        ? { closingRequirementValues: Object.values(values) }
        : {};
      if (category === "audit") {
        const response = await updateAuditProgress({
          ...buildAuditUpdate(
            record as Audit,
            data,
            customAttributes,
            attachments,
          ),
          ...closingRequirementValues,
        }).unwrap();
        dispatch(
          getAuditById(
            transformCustomAttributesDataMap(
              response.audit,
              customAttributes!,
              "YYYY-MM-DD",
            ),
          ),
        );
        return {
          outcome: "saved",
          warnings: response.closingRequirementWarnings,
        };
      }
      const response = await updateGaiaObjectProgress({
        ...buildGaiaObjectUpdate(
          record as GaiaObject,
          data,
          attribute,
          attachments,
        ),
        ...closingRequirementValues,
      }).unwrap();
      dispatch(getGaiaObjectById(response));
      return {
        outcome: "saved",
        warnings: response.closingRequirementWarnings ?? [],
      };
    } catch (error) {
      return toSaveFailure(error, `Failed to update ${category} progress`);
    }
  };

  const onValid = async (data: ProgressUpdateFormValues) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setIsSaving(true);
    setUnmetRequirements([]);
    if (isProgressUpdateFilesEnabled) {
      setUploadStatuses(
        new Map(
          files.map((file) => [
            file,
            uploaded.current.has(file) ? "success" : "pending",
          ]),
        ),
      );
    }
    const result = await save(data);
    savingRef.current = false;
    setIsSaving(false);
    if (result.outcome === "not-met") {
      setUnmetRequirements(result.unmetRequirements);
      setRecheck((count) => count + 1);
      return;
    }
    if (result.outcome === "failed") {
      addNotification({
        type: "error",
        header: result.message,
        dismissible: true,
        timeout: 10000,
      });
      return;
    }
    addNotification({
      type: "success",
      header: `${cap(category)} progress updated successfully`,
      dismissible: true,
      timeout: 10000,
    });
    if (result.warnings.length > 0) {
      addNotification({
        type: "warning",
        header: `${cap(category)} completed with ${result.warnings.length} closing requirement ${result.warnings.length === 1 ? "warning" : "warnings"}`,
        content: requirementList(result.warnings),
        dismissible: true,
      });
    }
    close();
  };

  const errorText = (name: keyof ProgressUpdateFormValues) =>
    get(formMethods.formState?.errors, `${name}.message`);

  return (
    <SplitPanel
      header="Progress update"
      i18nStrings={{
        closeButtonAriaLabel: "Close panel",
        openButtonAriaLabel: "Open panel",
      }}
      closeBehavior="hide"
      hidePreferencesButton
    >
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onValid)}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  formAction="none"
                  variant="link"
                  onClick={close}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={isSaving}
                  disabled={saveBlocked}
                >
                  Save
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              {unmetRequirements.length > 0 ? (
                <Alert
                  type="error"
                  header={`${cap(category)} cannot be completed yet`}
                >
                  {`${unmetRequirements.length} closing ${unmetRequirements.length === 1 ? "requirement is" : "requirements are"} not met:`}
                  {requirementList(unmetRequirements)}
                  {outcomeUnmet && (
                    <Link
                      variant="primary"
                      onFollow={(e) => {
                        e.preventDefault();
                        viewOutcomes();
                      }}
                    >
                      Go to outcomes
                    </Link>
                  )}
                </Alert>
              ) : checklistActive && blocking > 0 ? (
                <Alert
                  type="error"
                  header={`${cap(category)} cannot be completed yet`}
                >
                  {`${pluralise(blocking, "closing requirement", "closing requirements")} not met. Resolve the items below, or change the status.`}
                </Alert>
              ) : checklistActive && warnings > 0 ? (
                <Alert
                  type="warning"
                  header={`${cap(category)} can be completed`}
                >
                  {`${pluralise(warnings, "warning", "warnings")} will be recorded with the close.`}
                </Alert>
              ) : null}
              <ColumnLayout columns={2}>
                <FormField label="Status" errorText={errorText("status")}>
                  <ControlledSelect
                    name="status"
                    options={uiContent.auditStatusOptions}
                    control={formMethods.control}
                    filteringType="auto"
                  />
                </FormField>
                <FormField
                  label="Action pending"
                  errorText={errorText("statusActionOwner")}
                >
                  <ControlledSelect
                    name="statusActionOwner"
                    options={uiContent.actionOwnerOptions}
                    control={formMethods.control}
                    filteringType="auto"
                  />
                </FormField>
                {showCloseDate && (
                  <FormField
                    label={`${cap(category)} close date`}
                    description={`Record the date this ${category} closed.`}
                    errorText={errorText("closeDate")}
                  >
                    <ControlledDatePicker
                      name="closeDate"
                      control={formMethods.control}
                      placeholder="YYYY/MM/DD"
                    />
                  </FormField>
                )}
                <FormField
                  label="Administrative effort"
                  description="Est. actual administrative effort spent to close this case (in hours)."
                  errorText={errorText("effort")}
                >
                  <ControlledInput
                    name="effort"
                    type="number"
                    control={formMethods.control}
                  />
                </FormField>
              </ColumnLayout>
              <FormField
                label="Progress detail"
                description="Briefly describe the update."
                errorText={errorText("statusDetail")}
              >
                <ControlledTextarea
                  name="statusDetail"
                  rows={6}
                  control={formMethods.control}
                />
              </FormField>
              {checklistActive && (
                <ClosingRequirementsChecklist
                  category={category}
                  teamName={record?.teamName ?? ""}
                  recordId={documentsParentId ?? ""}
                  results={checkedResults}
                  requirements={requirements}
                  values={values}
                  onValueChange={onRequirementValueChange}
                  isEvaluating={isEvaluating}
                  evaluateError={evaluateError}
                  onViewOutcomes={viewOutcomes}
                />
              )}
              {isProgressUpdateFilesEnabled && (
                <FormField
                  label="Attachments"
                  description="Attach files to this progress update."
                >
                  <SpaceBetween size="xs">
                    <UploadFile files={files} setFiles={setFiles} multiple />
                    {files.map((file, i) => {
                      const uploadStatus = uploadStatuses.get(file);
                      return uploadStatus ? (
                        <StatusIndicator
                          key={`${file.name}-${i}`}
                          type={UPLOAD_STATUS_INDICATOR[uploadStatus]}
                        >
                          {file.name}
                        </StatusIndicator>
                      ) : null;
                    })}
                  </SpaceBetween>
                </FormField>
              )}
            </SpaceBetween>
          </Form>
        </form>
      </FormProvider>
    </SplitPanel>
  );
};

export default ProgressUpdateSplitPanel;
